import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useRef } from 'react';
import { cloneChildren, getOnEnter, getPositionInViewport } from '../../_utils';
import { Avatar } from '../../avatar';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { RoutedLink } from '../../link';
import { Colors, Typography } from '../../typography';
import { Menu } from '../menu';
import styles from '../menu.module.scss';
import { MenuVariant } from '../types';
export const MenuItem = ({ id, iconName, customIcon, label, children, data, keyPressed = _.noop, active = false, onSelect = _.noop, disabled = false, subText, rightText, hasRightIconInGroup = false, itemTooltip, itemDisabledTooltip, imagePath, variant = MenuVariant.DEFAULT, avatarSrc, rightStats, trackId, showLabelTooltip = false, labelTruncationLimit, hasError = false, href, enableRichSubtext = false, isThemeEnabled = false, ...restProps }) => {
    const sideNavVariant = [
        MenuVariant.SIDE_NAV,
        MenuVariant.MODAL_SIDE_NAV,
    ].includes(variant);
    const handleItemSelection = useCallback((event) => {
        if (!disabled) {
            event.stopPropagation();
            onSelect({
                id,
                label,
                data,
                imagePath,
                event,
            });
        }
    }, [data, disabled, id, imagePath, label, onSelect]);
    const menuRef = useRef(null);
    const itemSubMenuClasses = cx(styles.item, styles.itemContainer, styles.itemWithLeftIcon, children || rightText
        ? styles.itemWithRightIcon
        : styles.withOutRightIcons, {
        [styles.itemActive]: active && !disabled,
        [`${styles.itemDisabled}`]: disabled,
    });
    const labelClasses = cx(styles.itemText, !!iconName && !children
        ? styles.withOutLeftIcons
        : styles.itemWithLeftIcon);
    const typographyLabelClass = cx(styles.typographyLabel, {
        [styles.labelHighlight]: MenuVariant.SIDE_NAV && active,
        [styles.themeLabelHighlight]: variant === MenuVariant.SIDE_NAV && active && isThemeEnabled,
    });
    const renderLeftIcon = (name) => {
        let iconColor = customIcon ? customIcon.color : IconColor.GRAY;
        const iconSize = customIcon ? customIcon.size : IconSize.MEDIUM;
        if (sideNavVariant && active) {
            iconColor = IconColor.BLUE;
        }
        else if (sideNavVariant) {
            iconColor = IconColor.BLACK;
        }
        return (iconName && (React.createElement("div", { className: styles.itemIcon },
            React.createElement(Icon, { id: name, color: iconColor, size: sideNavVariant ? IconSize.LARGE : iconSize, className: customIcon ? customIcon.className : '' }))));
    };
    const renderAvatarIcon = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(Avatar, { name: label, src: avatarSrc, size: sideNavVariant ? 'xs' : 's', className: styles.avatarOverride })));
    };
    const hasSubMenu = () => !!children && children.type === Menu;
    const renderRightCaretIcon = () => {
        if (hasSubMenu() && !rightText) {
            return (React.createElement("div", { className: styles.cascadingIcon },
                React.createElement(Icon, { id: IconID.CARET_RIGHT, color: IconColor.GRAY, size: IconSize.MEDIUM })));
        }
        // Empty Icon added on right side to match the allignment with caret icon
        if (hasRightIconInGroup) {
            return (React.createElement("div", { className: styles.cascadingIcon },
                React.createElement(Icon, { id: IconID.EMPTY })));
        }
        return null;
    };
    const renderCustomContent = () => (React.createElement("div", { className: styles.item }, children));
    const setCascadingItemsDirection = () => {
        var _a, _b, _c;
        const cascadingMenuEl = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.children[1];
        if (!cascadingMenuEl)
            return;
        const hoveringMenuItemEl = menuRef === null || menuRef === void 0 ? void 0 : menuRef.current;
        const hoveringMenuTop = getPositionInViewport(hoveringMenuItemEl).top;
        const cascadingMenuTop = hoveringMenuTop - 8;
        const subMenuRect = (_c = (_b = menuRef.current) === null || _b === void 0 ? void 0 : _b.children[1]) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
        let topPosition;
        const overflowBottom = cascadingMenuTop + ((subMenuRect === null || subMenuRect === void 0 ? void 0 : subMenuRect.height) || 0) > window.innerHeight;
        if (overflowBottom) {
            const distanceToBottom = window.innerHeight - hoveringMenuTop;
            topPosition = `${(((subMenuRect === null || subMenuRect === void 0 ? void 0 : subMenuRect.height) || 0) - distanceToBottom) *
                -1}px`;
            cascadingMenuEl.style.top = topPosition;
        }
    };
    const renderSubMenu = () => hasSubMenu() &&
        !rightText &&
        cloneChildren(children, { isSubMenu: true, show: true });
    const getTooltipConfig = () => {
        if (showLabelTooltip) {
            return {
                dataTooltipContent: label,
                ...(labelTruncationLimit && {
                    disableTooltip: label.length < labelTruncationLimit,
                }),
            };
        }
        return undefined;
    };
    const renderLabel = () => (React.createElement(React.Fragment, null,
        React.createElement("div", { className: labelClasses },
            React.createElement(Typography, { variant: "p", className: typographyLabelClass, tooltipConfig: getTooltipConfig(), "data-testid": "menu-item-label-testid" }, label),
            !!subText && (React.createElement(Typography, { className: cx({
                    [styles.subText]: enableRichSubtext,
                }), variant: enableRichSubtext ? 'footnote' : 'p', color: enableRichSubtext ? Colors.info : Colors.grayLight }, subText))),
        !!rightText && (React.createElement("div", { className: styles.rightText },
            React.createElement(Typography, { variant: "p", color: Colors.grayLight }, rightText)))));
    const getErrorIcon = () => {
        return (React.createElement(Icon, { id: IconID.EXCLAMATION_POINT_CIRCLE, color: IconColor.RED, size: IconSize.SMALL, "data-testid": "menu-item-error-icon" }));
    };
    const renderChildren = () => (React.createElement(React.Fragment, null,
        !!children && children.type !== Menu ? (renderCustomContent()) : (React.createElement("div", { className: cx(styles.itemInfo, {
                [styles.modalSideNavVariant]: variant === MenuVariant.MODAL_SIDE_NAV,
            }), role: "tab", tabIndex: -1, onFocus: setCascadingItemsDirection, onMouseOver: setCascadingItemsDirection },
            avatarSrc ? renderAvatarIcon() : renderLeftIcon(iconName),
            renderLabel(),
            rightStats && (React.createElement("div", { className: styles.rightStats }, rightStats)),
            hasError && getErrorIcon(),
            renderRightCaretIcon())),
        renderSubMenu()));
    const linkProps = {
        href,
        noUderline: true,
    };
    const props = {
        className: itemSubMenuClasses,
        onClick: handleItemSelection,
        id,
        ref: menuRef,
        'data-testid': id,
        role: 'option',
        'aria-selected': active,
        tabIndex: 0,
        onKeyDown: getOnEnter(handleItemSelection, keyPressed),
        'data-tooltip-content': disabled ? itemDisabledTooltip : itemTooltip,
        'track-id': trackId,
        ...(href && linkProps),
        ...restProps,
    };
    const component = href ? RoutedLink : 'div';
    const element = React.createElement(component, props, renderChildren());
    return element;
};
//# sourceMappingURL=menu-item.js.map